import type { Logger as PinoLogger } from 'pino';

export type LogEvent = {
  event: string;
  message?: string;
} & Record<string, string | number | boolean | object | null | undefined>;

export const isLogEvent = (value: unknown): value is LogEvent => {
  return typeof value === 'object' && value !== null && 'event' in value;
};

export type ErrorLogEvent = {
  err?: Error;
};

export type LogFn<T extends LogEvent | string> = {
  (log: T): void;
};

export type LogMethods<T extends LogEvent> = {
  fatal: LogFn<T & ErrorLogEvent>;
  error: LogFn<T & ErrorLogEvent>;
  warn: LogFn<T & ErrorLogEvent>;
  info: LogFn<T>;
  debug: LogFn<T>;
};

export type Logger<T extends LogEvent> = LogMethods<T> & {
  /**
   * Keeps personal data that server-side logs would otherwise redact, for a
   * team that has a reason to log it. Secrets such as passwords, tokens and card
   * details are still redacted, and each record is marked `piiRedacted: false`.
   */
  raw: LogMethods<T>;
  child: (bindings: Record<string, unknown>) => Logger<T>;
} & Pick<PinoLogger, 'bindings'>;
